/**
 * Catálogos remotos contra a API própria.
 *
 * Espelha as assinaturas do antigo módulo Supabase (referência histórica).
 */
import { apiGet, apiPost, apiDelete, isCloudConfigured } from './client';
import type { CatalogFile } from '../../types/catalog';

export interface RemoteCatalog {
  id: string;
  user_id?: string;
  catalog_name?: string;
  catalogName?: string;
  vendor: string;
  family: string;
  imported_at?: string;
  importedAt?: string;
}

export interface RemoteModel {
  id?: string;
  catalog_id?: string;
  model_code: string;
  name: string;
  vendor: string;
  power_kw: number;
  nominal_current: number;
  voltage_class: string;
  rated_frequency: number;
  cooling_method: string;
  protection_rating: string;
  fan_count: number;
  max_temp: number;
  nominal_dc: number;
  part_number: string | null;
  ordering_number: string | null;
  frame_size: string | null;
  power_card_code: string | null;
  igbt_module_code: string | null;
  fan_code: string | null;
  capacitor_bank_code: string | null;
  rul_params: Record<string, number> | null;
  modbus_map: Record<string, number> | null;
  trip_codes: Record<string, { code: string; mask: number; desc: string }> | null;
}

/** Normaliza para o formato snake_case que os consumidores esperam. */
function normalizeCatalog(row: Record<string, unknown>): RemoteCatalog {
  return {
    id: String(row.id),
    catalog_name: String(row.catalogName ?? row.catalog_name ?? ''),
    vendor: String(row.vendor),
    family: String(row.family),
    imported_at: String(row.importedAt ?? row.imported_at ?? new Date().toISOString()),
  };
}

export async function listRemoteCatalogs(): Promise<RemoteCatalog[]> {
  if (!isCloudConfigured()) return [];
  const rows = await apiGet<Array<Record<string, unknown>>>('/api/catalogs');
  return rows.map(normalizeCatalog);
}

export async function listRemoteModels(catalogId: string): Promise<RemoteModel[]> {
  if (!isCloudConfigured()) return [];
  return apiGet<RemoteModel[]>(`/api/catalogs/${catalogId}/models`);
}

export async function importCatalogToRemote(
  catalog: CatalogFile,
): Promise<{ catalog: RemoteCatalog; skipped: boolean }> {
  const payload = {
    catalogName: catalog.catalogName,
    vendor: catalog.vendor,
    family: catalog.family,
    models: catalog.models,
  };

  const result = await apiPost<{ catalog: Record<string, unknown>; skipped: boolean }>(
    '/api/catalogs',
    payload,
  );

  return { catalog: normalizeCatalog(result.catalog), skipped: result.skipped };
}

export async function deleteRemoteCatalog(id: string): Promise<void> {
  if (!isCloudConfigured()) return;
  // O backend isola por user_id — não há como apagar catálogo de outro usuário
  // mesmo que o id seja enviado.
  await apiDelete(`/api/catalogs/${id}`);
}

function remoteModelToCatalogEntry(m: RemoteModel): CatalogFile['models'][number] {
  return {
    modelCode: m.model_code,
    family: '',
    name: m.name,
    vendor: m.vendor as CatalogFile['models'][number]['vendor'],
    powerKW: m.power_kw,
    nominalCurrent: m.nominal_current,
    voltageClass: m.voltage_class as CatalogFile['models'][number]['voltageClass'],
    ratedFrequency: m.rated_frequency,
    coolingMethod: m.cooling_method as CatalogFile['models'][number]['coolingMethod'],
    protectionRating: m.protection_rating,
    fanCount: m.fan_count,
    maxTemp: m.max_temp,
    nominalDC: m.nominal_dc,
    partNumber: m.part_number ?? undefined,
    orderingNumber: m.ordering_number ?? undefined,
    frameSize: m.frame_size ?? undefined,
    powerCardCode: m.power_card_code ?? undefined,
    igbtModuleCode: m.igbt_module_code ?? undefined,
    fanCode: m.fan_code ?? undefined,
    capacitorBankCode: m.capacitor_bank_code ?? undefined,
    rulParams: {
      fanMainLife: m.rul_params?.fanMainLife ?? 50000,
      fanControlLife: m.rul_params?.fanControlLife ?? 30000,
      capacitorBaseLife: m.rul_params?.capacitorBaseLife ?? 100000,
      igbtMaxCycles: m.rul_params?.igbtMaxCycles ?? 50000000,
      bearingBaseLife: m.rul_params?.bearingBaseLife ?? 40000,
    },
    modbusMap: {
      regFreq: m.modbus_map?.regFreq ?? 40001,
      regCurrent: m.modbus_map?.regCurrent ?? 40002,
      regDC: m.modbus_map?.regDC ?? 40003,
      regTemp: m.modbus_map?.regTemp ?? 40004,
      regWriteFreq: m.modbus_map?.regWriteFreq ?? 40022,
      poles: m.modbus_map?.poles ?? 2,
      currentScale: m.modbus_map?.currentScale ?? 1,
    },
    tripCodes: (m.trip_codes ?? {}) as CatalogFile['models'][number]['tripCodes'],
  };
}

export async function fetchAllRemoteCatalogs(): Promise<Array<{ key: string; catalog: CatalogFile }>> {
  if (!isCloudConfigured()) return [];

  const remoteCatalogs = await listRemoteCatalogs();
  const result: Array<{ key: string; catalog: CatalogFile }> = [];

  for (const rc of remoteCatalogs) {
    const models = await listRemoteModels(rc.id);
    const catalog: CatalogFile = {
      catalogName: rc.catalog_name ?? '',
      vendor: rc.vendor as CatalogFile['vendor'],
      family: rc.family,
      importedAt: rc.imported_at ?? new Date().toISOString(),
      models: models.map(remoteModelToCatalogEntry),
    };
    result.push({ key: `cloud-${rc.id}`, catalog });
  }

  return result;
}
